import { Button, Card, Form, Input, Select } from 'antd'
import styled, { css } from 'styled-components'

/**
 * Shared styled components for reporting sections.
 * These components follow the design system tokens and provide consistent
 * styling across Cover, Summary, Calibration, and other section pages.
 */

// =============================================================================
// FORM COMPONENTS
// =============================================================================

/**
 * StyledForm - Enhanced form with improved spacing and visual hierarchy
 * Used across all report section forms for consistent layout
 */
export const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 1.25rem;
  }

  .ant-form-item-label {
    padding-bottom: 0.375rem;

    > label {
      font-weight: 500;
      font-size: 0.875rem;
      color: ${({ theme }) => theme.forecast.activeText};

      &::before {
        color: ${({ theme }) => theme.forecast.danger || '#ff4d4f'} !important;
      }
    }
  }

  .ant-form-item-explain {
    font-size: 0.75rem;
    margin-top: 0.25rem;
    color: ${({ theme }) => theme.forecast.tertiaryText};
  }

  .ant-form-item-explain-error {
    color: ${({ theme }) => theme.forecast.danger || '#ff4d4f'};
  }
` as typeof Form

/**
 * FormSection - Groups related form fields with visual separation
 */
export const FormSection = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`

/**
 * FormSectionTitle - Section heading within forms
 */
export const FormSectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  padding: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  letter-spacing: -0.01em;
`

/**
 * FormSectionSubtitle - Secondary text for section descriptions
 */
export const FormSectionSubtitle = styled.p`
  margin: -0.5rem 0 1rem 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  line-height: 1.5;
`

/**
 * FormRow - Horizontal layout for inline form fields
 */
export const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  > * {
    flex: 1;
    min-width: 12rem;
  }
`

// =============================================================================
// BUTTON COMPONENTS
// =============================================================================

/**
 * GenerateButton - Primary action button with enhanced styling
 */
export const GenerateButton = styled(Button)`
  && {
    margin-top: 0.5rem;
    height: 2.5rem;
    font-weight: 500;
    font-size: 0.875rem;
    border-radius: 0.5rem;
    transition: all 0.2s ease;

    &:not(:disabled):hover {
      transform: translateY(-1px);
      box-shadow: 0 0.25rem 0.75rem
        ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.1)'};
    }

    &:not(:disabled):active {
      transform: translateY(0);
    }
  }
`

/**
 * PrimaryButton - Filled primary action button
 */
export const PrimaryButton = styled(Button)`
  && {
    height: 2.5rem;
    font-weight: 500;
    font-size: 0.875rem;
    border-radius: 0.5rem;
    background: ${({ theme }) => theme.forecast.primary || '#1890ff'};
    border-color: ${({ theme }) => theme.forecast.primary || '#1890ff'};
    color: #fff;
    transition: all 0.2s ease;

    &:not(:disabled):hover {
      background: ${({ theme }) => theme.forecast.highlight || '#40a9ff'};
      border-color: ${({ theme }) => theme.forecast.highlight || '#40a9ff'};
      transform: translateY(-1px);
      box-shadow: 0 0.25rem 0.75rem
        ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.15)'};
    }

    &:not(:disabled):active {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
    }
  }
`

/**
 * SecondaryButton - Ghost/outline style button
 */
export const SecondaryButton = styled(Button)`
  && {
    height: 2.5rem;
    font-weight: 500;
    font-size: 0.875rem;
    border-radius: 0.5rem;
    background: ${({ theme }) => theme.forecast.defaultButton || 'transparent'};
    border: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    color: ${({ theme }) => theme.forecast.activeText};
    transition: all 0.2s ease;

    &:not(:disabled):hover {
      background: ${({ theme }) =>
        theme.forecast.hoverButton || 'rgba(0, 0, 0, 0.04)'};
      border-color: ${({ theme }) => theme.forecast.background4};
      color: ${({ theme }) => theme.forecast.activeText};
    }

    &:not(:disabled):active {
      background: ${({ theme }) =>
        theme.forecast.activeButton || 'rgba(0, 0, 0, 0.08)'};
    }
  }
`

/**
 * DangerButton - Destructive action button
 */
export const DangerButton = styled(Button)`
  && {
    height: 2.5rem;
    font-weight: 500;
    font-size: 0.875rem;
    border-radius: 0.5rem;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.forecast.danger || '#ff4d4f'};
    color: ${({ theme }) => theme.forecast.danger || '#ff4d4f'};
    transition: all 0.2s ease;

    &:not(:disabled):hover {
      background: ${({ theme }) =>
        theme.forecast.redGlassBackground || 'rgba(255, 77, 79, 0.1)'};
      border-color: ${({ theme }) => theme.forecast.danger || '#ff4d4f'};
      color: ${({ theme }) => theme.forecast.danger || '#ff4d4f'};
    }
  }
`

// =============================================================================
// CARD COMPONENTS
// =============================================================================

/**
 * SectionCard - Card container for report sections with subtle elevation
 */
export const SectionCard = styled(Card)`
  && {
    background: ${({ theme }) =>
      theme.forecast.cardBackground || theme.forecast.background1};
    border: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    border-radius: 0.75rem;
    box-shadow: 0 0.125rem 0.5rem
      ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.06)'};
    overflow: hidden;

    .ant-card-head {
      border-bottom: 1px solid
        ${({ theme }) =>
          theme.forecast.cardDividerBackground || theme.forecast.border};
      padding: 0 1.5rem;
      min-height: 3.5rem;
      background: ${({ theme }) =>
        theme.forecast.cardGroupBackground || 'transparent'};
    }

    .ant-card-head-title {
      font-weight: 600;
      font-size: 0.9375rem;
      color: ${({ theme }) => theme.forecast.activeText};
      padding: 1rem 0;
    }

    .ant-card-body {
      padding: 1.5rem;
    }

    .ant-card-extra {
      padding: 1rem 0;
    }
  }
`

/**
 * CompactCard - Smaller card variant for nested content
 */
export const CompactCard = styled(Card)`
  && {
    background: ${({ theme }) =>
      theme.forecast.cardBackground || theme.forecast.background1};
    border: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    border-radius: 0.5rem;
    margin-bottom: 1rem;

    .ant-card-head {
      border-bottom: 1px solid
        ${({ theme }) =>
          theme.forecast.cardDividerBackground || theme.forecast.border};
      padding: 0 1rem;
      min-height: 2.75rem;
    }

    .ant-card-head-title {
      font-weight: 500;
      font-size: 0.875rem;
      color: ${({ theme }) => theme.forecast.activeText};
      padding: 0.75rem 0;
    }

    .ant-card-body {
      padding: 1rem;
    }
  }
`

/**
 * InfoCard - Highlighted card for important information
 */
export const InfoCard = styled.div`
  background: ${({ theme }) =>
    theme.forecast.blueGlassBackground || 'rgba(24, 144, 255, 0.08)'};
  border: 1px solid
    ${({ theme }) => theme.forecast.primary || 'rgba(24, 144, 255, 0.3)'};
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
`

/**
 * WarningCard - Card for warning messages
 */
export const WarningCard = styled.div`
  background: ${({ theme }) =>
    theme.forecast.greenGlassBackground || 'rgba(250, 173, 20, 0.08)'};
  border: 1px solid ${({ theme }) => theme.forecast.warning || '#faad14'};
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
`

// =============================================================================
// INPUT COMPONENTS
// =============================================================================

/**
 * StyledInput - Enhanced input field styling
 */
export const StyledInput = styled(Input)`
  && {
    border-radius: 0.375rem;
    border-color: ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
    background: ${({ theme }) =>
      theme.forecast.cardBackground || theme.forecast.background1};
    color: ${({ theme }) => theme.forecast.activeText};
    transition: all 0.2s ease;

    &:hover {
      border-color: ${({ theme }) => theme.forecast.background4};
    }

    &:focus,
    &:focus-within {
      border-color: ${({ theme }) => theme.forecast.primary || '#1890ff'};
      box-shadow: 0 0 0 2px
        ${({ theme }) =>
          theme.forecast.blueGlassBackground || 'rgba(24, 144, 255, 0.2)'};
    }

    &::placeholder {
      color: ${({ theme }) => theme.forecast.tertiaryText};
    }

    &:disabled {
      background: ${({ theme }) =>
        theme.forecast.cardGroupBackground || theme.forecast.background2};
      color: ${({ theme }) => theme.forecast.textDisabled};
    }
  }
`

/**
 * StyledSelect - Enhanced select field styling
 */
export const StyledSelect = styled(Select)`
  && {
    .ant-select-selector {
      border-radius: 0.375rem !important;
      border-color: ${({ theme }) =>
        theme.forecast.cardDividerBackground ||
        theme.forecast.border} !important;
      background: ${({ theme }) =>
        theme.forecast.cardBackground || theme.forecast.background1} !important;
      transition: all 0.2s ease;
    }

    &:hover .ant-select-selector {
      border-color: ${({ theme }) => theme.forecast.background4} !important;
    }

    &.ant-select-focused .ant-select-selector {
      border-color: ${({ theme }) =>
        theme.forecast.primary || '#1890ff'} !important;
      box-shadow: 0 0 0 2px
        ${({ theme }) =>
          theme.forecast.blueGlassBackground ||
          'rgba(24, 144, 255, 0.2)'} !important;
    }

    .ant-select-selection-item {
      color: ${({ theme }) => theme.forecast.activeText};
    }

    .ant-select-selection-placeholder {
      color: ${({ theme }) => theme.forecast.tertiaryText};
    }
  }
` as typeof Select

// =============================================================================
// TYPOGRAPHY COMPONENTS
// =============================================================================

/**
 * PageTitle - Main page heading
 */
export const PageTitle = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  letter-spacing: -0.02em;
  line-height: 1.3;
`

/**
 * SectionTitle - Section heading within pages
 */
export const SectionTitle = styled.h2`
  margin: 1.5rem 0 1rem 0;
  padding-top: 1.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  letter-spacing: -0.01em;
  border-top: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};

  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`

/**
 * SubsectionTitle - Smaller heading for subsections
 */
export const SubsectionTitle = styled.h3`
  margin: 1.25rem 0 0.75rem 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

/**
 * LabelText - Form label style text
 */
export const LabelText = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

/**
 * BodyText - Standard body text
 */
export const BodyText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.activeText};
  line-height: 1.6;
`

/**
 * SecondaryText - Muted secondary text
 */
export const SecondaryText = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  line-height: 1.5;
`

/**
 * HelpText - Small helper text
 */
export const HelpText = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  line-height: 1.4;
`

// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

/**
 * PageContainer - Main page wrapper with proper spacing
 */
export const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 64rem;
  margin: 0 auto;
`

/**
 * ContentSection - Vertical section with spacing
 */
export const ContentSection = styled.section`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`

/**
 * FlexRow - Horizontal flex container
 */
export const FlexRow = styled.div<{
  $gap?: string
  $align?: string
  $justify?: string
}>`
  display: flex;
  flex-direction: row;
  align-items: ${({ $align }) => $align || 'center'};
  justify-content: ${({ $justify }) => $justify || 'flex-start'};
  gap: ${({ $gap }) => $gap || '1rem'};
  flex-wrap: wrap;
`

/**
 * FlexColumn - Vertical flex container
 */
export const FlexColumn = styled.div<{ $gap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap || '0.75rem'};
`

/**
 * Grid - Responsive grid container
 */
export const Grid = styled.div<{ $columns?: number; $gap?: string }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(
      ${({ $columns }) => ($columns ? `${100 / $columns}%` : '16rem')},
      1fr
    )
  );
  gap: ${({ $gap }) => $gap || '1rem'};
`

// =============================================================================
// DIVIDER COMPONENTS
// =============================================================================

/**
 * Divider - Horizontal separator
 */
export const Divider = styled.hr`
  border: none;
  border-top: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
  margin: 1.5rem 0;
`

/**
 * VerticalDivider - Vertical separator for inline content
 */
export const VerticalDivider = styled.div`
  width: 1px;
  height: 1.5rem;
  background: ${({ theme }) =>
    theme.forecast.cardDividerBackground || theme.forecast.border};
  margin: 0 0.75rem;
`

// =============================================================================
// STATUS & BADGE COMPONENTS
// =============================================================================

/**
 * StatusBadge - Generic status indicator
 */
export const StatusBadge = styled.span<{
  $variant?: 'success' | 'warning' | 'error' | 'info' | 'default'
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'success':
        return css`
          background: ${theme.forecast.greenGlassBackground ||
          'rgba(82, 196, 26, 0.1)'};
          color: #52c41a;
        `
      case 'warning':
        return css`
          background: rgba(250, 173, 20, 0.1);
          color: ${theme.forecast.warning || '#faad14'};
        `
      case 'error':
        return css`
          background: ${theme.forecast.redGlassBackground ||
          'rgba(255, 77, 79, 0.1)'};
          color: ${theme.forecast.danger || '#ff4d4f'};
        `
      case 'info':
        return css`
          background: ${theme.forecast.blueGlassBackground ||
          'rgba(24, 144, 255, 0.1)'};
          color: ${theme.forecast.primary || '#1890ff'};
        `
      default:
        return css`
          background: ${theme.forecast.cardGroupBackground ||
          'rgba(0, 0, 0, 0.04)'};
          color: ${theme.forecast.secondaryText};
        `
    }
  }}
`

// =============================================================================
// ACTION BAR COMPONENTS
// =============================================================================

/**
 * ActionBar - Fixed or inline action buttons container
 */
export const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

/**
 * ActionBarStart - Left-aligned action bar
 */
export const ActionBarStart = styled(ActionBar)`
  justify-content: flex-start;
`

/**
 * ActionBarSpaceBetween - Space between action bar
 */
export const ActionBarSpaceBetween = styled(ActionBar)`
  justify-content: space-between;
`

// =============================================================================
// EMPTY STATE COMPONENTS
// =============================================================================

/**
 * EmptyState - Container for empty state messages
 */
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: ${({ theme }) => theme.forecast.tertiaryText};

  svg {
    width: 3rem;
    height: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`

/**
 * EmptyStateTitle - Title for empty state
 */
export const EmptyStateTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
`

/**
 * EmptyStateDescription - Description for empty state
 */
export const EmptyStateDescription = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  max-width: 24rem;
`
